import BellIcon from 'components/icons/Bell';
import MessageIcon from 'components/icons/Message';
import { FC } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import AdminNotifications from 'types/notifications/AdminNotifications';
import WebSocketManager from 'utils/websocket-manager';
import { List } from '.';
import ChevronRightIcon from '../icons/ChevronRight';
import LogoutIcon from '../icons/Logout';
import SettingsIcon from '../icons/Settings';
import NavigationLink from './components/NavigationLink';

const GlobalContainer = styled.div`
  margin-top: auto;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
`;

const AccountContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  border-top: 1px solid var(--gray-200);
  padding-top: 1.5rem;
  font-size: 0.9rem;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  font-size: inherit;
`;

const Name = styled.div`
  font-weight: 500;
  color: var(--gray-700);
  font-size: inherit;
  margin-bottom: 0.2rem;
`;

const Email = styled.div`
  font-weight: 400;
  color: var(--gray-500);
  font-size: 0.9em;
`;

const Logout = styled.button`
  border: none;
  background: transparent;
  stroke: var(--gray-500);
  border-radius: 2px;
  transition: 0.2s;
  padding: 0.3rem;
  &:hover {
    background-color: var(--gray-200);
  }
`;

const Login = styled(Link)`
  color: var(--primary-700);
  stroke: var(--primary-700);
  border-bottom: 1px solid transparent;
  svg {
    height: 0.7rem;
  }
  &:hover {
    border-bottom: 1px solid var(--primary-600);
  }
`;

const User: FC = () => {
  const navigate = useNavigate();

  const { data, error } = useSWR('/auth/profile');
  const { data: admin, error: adminError } = useSWR('/auth/admin');
  const { data: adminNotifications } = useSWR<AdminNotifications>(
    '/user/notification/admin',
  );

  const handleLogout = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('accessToken');
      mutate('/auth/profile', undefined);
      navigate('/authentication/login', undefined);
      WebSocketManager.logout();
    }
  };

  return (
    <GlobalContainer>
      <List>
        {!!admin && !adminError && (
          <NavigationLink
            icon={<BellIcon />}
            title="Actions à effectuer"
            href="/admin/actions/"
            activeLink="/admin/actions"
            notifications={
              adminNotifications
                ? adminNotifications.interruptionRequest +
                  adminNotifications.menteeResetStatus +
                  adminNotifications.mentorFormation
                : 0
            }
          />
        )}
        <NavigationLink
          icon={<MessageIcon />}
          title="Contacter l'Institut"
          href="/contact"
        />
        <NavigationLink
          icon={<SettingsIcon />}
          title="Paramètres"
          href="/settings/"
          activeLink="/settings"
        />
      </List>
      <AccountContainer>
        {error ? (
          <Login to="/authentication/login">
            Connexion <ChevronRightIcon />
          </Login>
        ) : data ? (
          <>
            <Info>
              <Name>{data && `${data.firstName} ${data.lastName}`}</Name>
              <Email>{data?.email}</Email>
            </Info>
            <Logout onClick={handleLogout}>
              <LogoutIcon />
            </Logout>
          </>
        ) : null}
      </AccountContainer>
    </GlobalContainer>
  );
};

export default User;
